<template>
    <div class="modern-card d-flex flex-column" id="barometer">
        <div class="card-header">
            <h2 class="card-title text-center mb-0">Stimmungsbarometer</h2>
        </div>

        <div class="card-content text-center flex-grow-1 d-flex flex-column justify-content-start">
            <barometer-v2 :rating="data.VotingResult" :resultModel="data" @revealed="onRevealed"></barometer-v2>

            <div v-if="!data.Loaded" class="mt-4">
                <button class="modern-btn" disabled>
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Lade Daten
                </button>
            </div>

            <div v-if="data.Loaded && data.Votes == 0" class="mt-4">
                <p class="mb-0">In den letzten 30 Tagen wurde keine Stimme abgegeben.</p>
            </div>

            <div v-if="data.Loaded && data.Votes > 0 && !data.ShowVotingResult" class="mt-4">
                <button @click="onShowVotingResult" :disabled="isRevealing" class="modern-btn">
                    <span v-if="isRevealing" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Ergebnis anzeigen
                </button>
            </div>
        </div>

        <!-- Emoji Rain Animation Container -->
        <div v-if="showEmojiRain" class="emoji-rain-container">
            <div v-for="n in 15" :key="n" class="emoji-rain">{{ selectedEmoji }}</div>
        </div>
    </div>
</template>

<script>
//import { CircleProgressBar } from 'circle-progress.vue';
import BarometerV2 from './BarometerV2';
import axios from 'axios';
import { onMounted, reactive, ref, computed, nextTick } from 'vue';
import emitter from '../event-emitter';
import { ratingOptions } from '../ratingData.js';

export default {
    props: [],
    components: {
        BarometerV2
    },
    mounted() {

    },
    setup() {
        const data = reactive({
            AvgScore: null,
            Votes: null,
            ShowVotingResult: false,
            VotingResult: 0,
            Trend: null,
            Motm: null,
            Loaded: false,
            LoadedAt: null // Zeitpunkt des letzten erfolgreichen getVotingSummary-Requests
        });

        // Ab diesem Alter gilt das geladene Ergebnis als veraltet und wird vor dem Reveal
        // neu geladen (Edge Case: Tab war laenger offen als der Wert relevant ist).
        const STALE_AFTER_MS = 24 * 60 * 60 * 1000;
        const isSummaryStale = () => !data.LoadedAt || (Date.now() - data.LoadedAt) > STALE_AFTER_MS;

        const showEmojiRain = ref(false);
        // Zeigt den Spinner auf dem Button, waehrend BarometerV2 seine Nadel-Animation faehrt
        // (siehe onShowVotingResult/onRevealed) - der Button bleibt so sichtbar, statt beim Klick
        // sofort zu verschwinden, und macht erst Platz, wenn der Wert tatsaechlich zu sehen ist.
        const isRevealing = ref(false);

        // Calculate emoji based on VotingResult
        const selectedEmoji = computed(() => {
            if (data.VotingResult && data.VotingResult > 0) {
                const roundedScore = Math.round(data.VotingResult);
                const emojiIndex = Math.max(0, Math.min(9, roundedScore - 1)); // Ensure index is between 0-9
                return ratingOptions[emojiIndex].emoji;
            }
            return "😊"; // Default emoji
        });

        emitter.on("ShowResultNow", async _ => {
            console.log(_);
            if (isSummaryStale()) {
                await loadVotingSummary();
            }
            data.VotingResult = data.AvgScore;
            data.ShowVotingResult = true;

            showEmojiRain.value = true;
            setTimeout(() => {
                showEmojiRain.value = false;
            }, 6500);
        });

        const scrollToResults = async () => {
            await nextTick();
            setTimeout(() => {
                const fbDoc = document.getElementById('barometer');
                if (fbDoc) {
                    fbDoc.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }, 300);

            // Trigger emoji rain animation
            showEmojiRain.value = true;
            setTimeout(() => {
                showEmojiRain.value = false;
            }, 6500); // Hide after animation completes
        };

        const onShowVotingResult = async () => {
            isRevealing.value = true;
            // Ist das beim Seitenaufruf geladene Ergebnis zu alt (siehe isSummaryStale), erst
            // neu laden - der Spinner auf dem Button deckt diese Wartezeit mit ab.
            if (isSummaryStale()) {
                await loadVotingSummary();
            }
            // Setzt nur den Rating-Wert - das loest BarometerV2's eigene Nadel-Animation aus
            // (watch auf die rating-Prop). data.ShowVotingResult bleibt bewusst false, bis
            // onRevealed() feuert, damit der Button bis dahin sichtbar bleibt (siehe Template).
            data.VotingResult = data.AvgScore;
            scrollToResults();
        };

        // Feuert, wenn BarometerV2 seine Animation abgeschlossen hat (siehe @revealed im Template).
        const onRevealed = () => {
            isRevealing.value = false;
            data.ShowVotingResult = true;
        };
        
        const loadVotingSummary = async () => {
            try {
                const response = await axios.get(process.env.VUE_APP_BACKEND_URL + '/stimmungsbarometer/getVotingSummary');
                // Update data properties
                data.AvgScore = response.data.avgScore;
                data.Votes = response.data.votes;
                data.Trend = response.data.trend;
                data.Motm = response.data.motm;
                data.Loaded = true;
                data.LoadedAt = Date.now();
                emitter.emit("OnVotingSummaryRequested", response.data);
            } catch (error) {
                console.error("Error fetching value:", error);
                data.Loaded = true;
            }
        };

        onMounted(() => {
            loadVotingSummary();
        });
        return {
            data,
            onShowVotingResult,
            onRevealed,
            isRevealing,
            showEmojiRain,
            selectedEmoji
        };
    }
}
</script>

<style scoped>
/* Nur der Abstand NACH der Trennlinie, nicht die Trennlinie selbst - padding-bottom bleibt
   unveraendert, damit Headline/Strich weiterhin auf gleicher Hoehe wie bei VotingForm liegen.
   Bewusst nur fuer diese Karte, .card-header wird auch von MoodHistoryCard, VotingForm und
   WelcomeHeader genutzt. */
#barometer .card-header {
    margin-bottom: 0;
}
</style>
